import { apiClient } from './apiClient';

export type DashboardSettingsSection = {
  cacheSeconds: number;
};

export type SmtpSettingsSection = {
  host: string;
  port: number;
  username: string;
  password: string;
  useTls: boolean;
  fromAddress: string;
  fromName: string;
};

export type ImapSettingsSection = {
  host: string;
  port: number;
  username: string;
  password: string;
  useSsl: boolean;
};

export type AuthenticationSettingsSection = {
  allowEmailPassword: boolean;
  allowGoogle: boolean;
  googleAllowedDomain: string;
  googleClientId: string;
  googleExtensionClientId: string;
};

export type TaskNotificationSettingsSection = {
  enabled: boolean;
  notifyTaskOwner: boolean;
  notifyTaskExecutor: boolean;
  notifyTeamBosses: boolean;
  notifyDirectBoss: boolean;
  allowUserPreferences: boolean;
};

export type TaskQueueSettingsSection = {
  dashboardBaseUrl: string;
  username: string;
  password: string;
};

export type NotesSettingsSection = {
  maxAttachments: number;
  maxAttachmentBytes: number;
};

export type CustomerSuccessSettingsSection = {
  maxAttachments: number;
  maxAttachmentBytes: number;
  allowedExtensions: string[];
  publicAppBaseUrl: string;
  welcomeEnabled: boolean;
  welcomeEmailTemplateId: string | null;
  csatEnabled: boolean;
  csatSurveyFormId: string | null;
  npsEnabled: boolean;
  npsSurveyFormId: string | null;
  npsFrequency: 'Daily' | 'Weekly' | 'Monthly' | 'Yearly';
  includeCannedInAiSuggest: boolean;
};

export type TenantSystemSettings = {
  dashboard: DashboardSettingsSection;
  smtp: SmtpSettingsSection;
  imap: ImapSettingsSection;
  authentication: AuthenticationSettingsSection;
  taskNotifications: TaskNotificationSettingsSection;
  taskQueue: TaskQueueSettingsSection;
  notes: NotesSettingsSection;
  customerSuccess: CustomerSuccessSettingsSection;
};

const DEFAULT_CUSTOMER_SUCCESS: CustomerSuccessSettingsSection = {
  maxAttachments: 10,
  maxAttachmentBytes: 52_428_800,
  allowedExtensions: [
    '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.txt', '.md',
    '.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg',
    '.mp3', '.wav', '.ogg', '.m4a', '.aac', '.webm', '.mp4',
    '.zip', '.rar', '.7z',
  ],
  publicAppBaseUrl: '',
  welcomeEnabled: true,
  welcomeEmailTemplateId: null,
  csatEnabled: true,
  csatSurveyFormId: null,
  npsEnabled: true,
  npsSurveyFormId: null,
  npsFrequency: 'Monthly',
  includeCannedInAiSuggest: true,
};

export async function fetchSystemSettings(): Promise<TenantSystemSettings> {
  const response = await apiClient.get<TenantSystemSettings>('/v1/admin/settings');
  return normalizeSystemSettings(response.data);
}

export async function saveSystemSettings(payload: TenantSystemSettings): Promise<TenantSystemSettings> {
  const response = await apiClient.put<TenantSystemSettings>('/v1/admin/settings', payload);
  return normalizeSystemSettings(response.data);
}

function normalizeSystemSettings(settings: TenantSystemSettings): TenantSystemSettings {
  return {
    ...settings,
    authentication: settings.authentication ?? {
      allowEmailPassword: true,
      allowGoogle: true,
      googleAllowedDomain: '',
      googleClientId: '',
      googleExtensionClientId: '',
    },
    taskNotifications: settings.taskNotifications ?? {
      enabled: true,
      notifyTaskOwner: true,
      notifyTaskExecutor: true,
      notifyTeamBosses: true,
      notifyDirectBoss: true,
      allowUserPreferences: true,
    },
    taskQueue: settings.taskQueue ?? {
      dashboardBaseUrl: '',
      username: '',
      password: '',
    },
    notes: settings.notes ?? {
      maxAttachments: 10,
      maxAttachmentBytes: 10_485_760,
    },
    customerSuccess: {
      ...DEFAULT_CUSTOMER_SUCCESS,
      ...(settings.customerSuccess ?? {}),
      allowedExtensions:
        settings.customerSuccess?.allowedExtensions?.length
          ? settings.customerSuccess.allowedExtensions
          : DEFAULT_CUSTOMER_SUCCESS.allowedExtensions,
      welcomeEmailTemplateId: settings.customerSuccess?.welcomeEmailTemplateId ?? null,
      csatSurveyFormId: settings.customerSuccess?.csatSurveyFormId ?? null,
      npsSurveyFormId: settings.customerSuccess?.npsSurveyFormId ?? null,
    },
  };
}
